<template>
  <v-dialog v-model="isOpen" width="60%" v-if="bookings && booking.room">
    <v-card>
      <v-form>
        <v-card-title class="pb-0">
          <span class="text-bold"> {{ title }} </span>
        </v-card-title>

        <v-card-text class="pb-0">
          <v-stepper v-model="step" elevation="0" class="elevation-0">
            <v-stepper-header elevation="0" class="elevation-0">
              <v-stepper-step
                :complete="step > 1"
                step="1"
                color="red lighten-1"
              >
                {{ $t("booking.booking") }}
              </v-stepper-step>

              <v-divider />

              <v-stepper-step
                :complete="step > 2"
                step="2"
                color="red lighten-1"
              >
                {{ $t("booking.desks") }}
              </v-stepper-step>

              <v-divider />

              <v-stepper-step step="3" color="red lighten-1">
                {{ $t("booking.equipments") }}
              </v-stepper-step>
            </v-stepper-header>

            <v-stepper-items>
              <v-stepper-content step="1" class="pt-0 pb-0">
                <BookingStep
                  ref="bookingStep"
                  :booking="booking"
                  :desksAvailability="desksAvailability"
                  :equipmentsAvailability="equipmentsAvailability"
                  @cancelBooking="close"
                  @goNext="goNext"
                />
              </v-stepper-content>

              <v-stepper-content step="2" class="pt-0 pb-0">
                <ChooseDesksStep
                  ref="chooseDesksStep"
                  :booking="booking"
                  :desksAvailability="desksAvailability"
                  :canGoBack="canGoBack"
                  @goBack="goBack"
                  @goNext="goNext"
                />
              </v-stepper-content>

              <v-stepper-content step="3" class="pt-0 pb-0">
                <ChooseEquipmentsStep
                  ref="chooseEquipmentsStep"
                  :booking="booking"
                  :equipmentsAvailability="equipmentsAvailability"
                  :canGoBack="canGoBack"
                  @goBack="goBack"
                  @showSummary="showSummary"
                />
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-card-text>
      </v-form>
    </v-card>

    <SummaryDialog
      ref="summaryDialog"
      :booking="booking"
      @createBooking="createBooking"
    />
  </v-dialog>
</template>

<script>
import BookingStep from "./roomBookingDialog/BookingStep";
import ChooseDesksStep from "./roomBookingDialog/ChooseDesksStep";
import ChooseEquipmentsStep from "./roomBookingDialog/ChooseEquipmentsStep";
import SummaryDialog from "./roomBookingDialog/SummaryDialog";
import { mapGetters, mapActions } from "vuex";
import i18n from "@/i18n";

const defaultBooking = {
  room: null,
  interval: null,
  readableDates: null,
  numUsers: null,
  reason: null,
  readableReason: null,
  complement: null,
  desks: [],
  wholeRoom: null,
  equipments: [],
};

export default {
  name: "RoomBookingDialog",
  components: {
    BookingStep,
    ChooseDesksStep,
    ChooseEquipmentsStep,
    SummaryDialog,
  },
  data: () => ({
    isOpen: false,
    step: null,
    booking: { ...defaultBooking },
    desksAvailability: { value: false },
    equipmentsAvailability: { value: false },
  }),
  computed: {
    ...mapGetters("bookingModule", {
      bookings: "getBookings",
    }),

    title() {
      return "%1 - %2 %3"
        .replace("%1", this.booking.room.center.name)
        .replace("%2", i18n.t("booking.bookRoom"))
        .replace("%3", this.booking.room.name);
    },
    canGoBack() {
      return this.step && this.step > 1;
    },
  },
  methods: {
    ...mapActions("bookingModule", ["addBooking"]),

    clear() {
      this.$refs.bookingStep.reset();
      this.$refs.chooseDesksStep.reset();
      this.$refs.chooseEquipmentsStep.reset();

      this.step = null;
      this.booking = { ...defaultBooking };
    },
    open(room) {
      this.booking.room = room;

      this.isOpen = true;
      this.step = 1;

      this.$nextTick(() => {
        this.$refs.chooseDesksStep.updateAvailability();
        this.$refs.chooseEquipmentsStep.updateAvailability();
      });
    },
    close() {
      this.isOpen = false;
    },
    goBack() {
      --this.step;
    },
    goNext() {
      ++this.step;
    },
    showSummary() {
      this.$refs.summaryDialog.open();
    },
    createBooking() {
      this.addBooking({
        dateStart: this.booking.interval.start,
        dateEnd: this.booking.interval.end,
        resources: [
          this.booking.room,
          ...this.booking.desks,
          ...this.booking.equipments,
        ],
        center: this.booking.room.center,
        numUsers: this.booking.numUsers,
        reason: this.booking.reason,
        complement: this.booking.complement,
      });

      this.close();
    },
  },
  watch: {
    // clear forms when dialog is closed
    isOpen(newValue) {
      if (!newValue) this.clear();
    },
    // notify step component that it is shown or hidden
    step(newValue, oldValue) {
      this.$nextTick(() => {
        const pages = [
          this.$refs.bookingStep,
          this.$refs.chooseDesksStep,
          this.$refs.chooseEquipmentsStep,
        ];
        if (oldValue) pages[oldValue - 1]?.hide();
        if (newValue) pages[newValue - 1]?.show();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.text-bold {
  font-weight: 500;
}
</style>
